// main.js - malá ukázka: zavírání flash zpráv + theme init
import theme from './theme.js';
import 'bootstrap/dist/js/bootstrap.bundle';
import { initTiptapEditor } from './tiptap-editor.js';
import Chart from 'chart.js/auto';

// Export initTiptapEditor pro použití v templates
window.initTiptapEditor = initTiptapEditor;

function getCssVar(name, fallback = '') {
  try {
    const from = (el) => {
      const v = getComputedStyle(el).getPropertyValue(name);
      return (v || '').trim();
    };

    let v = from(document.documentElement);
    if (!v || v.startsWith('var(')) v = from(document.body);
    if (!v || v.startsWith('var(')) return fallback;
    return v;
  } catch (e) {
    return fallback;
  }
}

function getCssRgbVar(nameRgb, fallbackCssColor) {
  const v = getCssVar(nameRgb, '').trim();
  if (!v) return fallbackCssColor;
  // Bootstrap exposes e.g. "--bs-success-rgb: 25,135,84"
  if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(v)) return `rgb(${v})`;
  // If it is already a color string (hex/rgb), just pass through.
  return v;
}

function getBootstrapColor(name, fallbackCssColor) {
  // Prefer solid color tokens if available (e.g. --bs-success: #198754)
  const solid = getCssVar(`--bs-${name}`, '').trim();
  if (solid && !solid.startsWith('var(')) return solid;
  return getCssRgbVar(`--bs-${name}-rgb`, fallbackCssColor);
}

function initActivityCharts(root = document) {
  try {
    const dataEls = root.querySelectorAll('script[data-activity-chart]');
    if (!dataEls || !dataEls.length) return;

    dataEls.forEach((dataEl) => {
      const container = dataEl.parentElement;
      if (!container) return;
      const canvas = container.querySelector('.js-activity-chart');
      if (!canvas) return;

      let payload;
      try {
        payload = JSON.parse((dataEl.textContent || '').trim() || '{}');
      } catch (e) {
        return;
      }
      if (!payload || !Array.isArray(payload.labels)) return;

      // Prevent double init
      if (canvas.__chartInstance) {
        try { canvas.__chartInstance.destroy(); } catch (e) {}
        canvas.__chartInstance = null;
      }

      const viewsColor = getBootstrapColor('success', '#198754');
      const editsColor = getBootstrapColor('danger', '#dc3545');
      const grid = getCssVar('--panel-border', 'rgba(255,255,255,0.12)');
      const muted = getCssVar('--muted', '#9aa6b2');

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.__chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: payload.labels,
          datasets: [
            {
              label: 'Čtení',
              data: payload.views || [],
              borderColor: viewsColor,
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0.25,
            },
            {
              label: 'Editace',
              data: payload.edits || [],
              borderColor: editsColor,
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0.25,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                title: (items) => {
                  const i = items && items[0] ? items[0].dataIndex : 0;
                  return payload.labels && payload.labels[i] ? payload.labels[i] : '';
                },
              },
            },
          },
          scales: {
            x: {
              ticks: {
                color: muted,
                maxTicksLimit: 10,
                autoSkip: true,
              },
              grid: { color: grid },
            },
            y: {
              beginAtZero: true,
              ticks: { color: muted, precision: 0 },
              grid: { color: grid },
            },
          },
        },
      });
    });
  } catch (e) {
    // no-op
  }
}

// backwards compatibility (starý název)
window.initShareActivityChart = initActivityCharts;

function autoDismissAlerts(root = document) {
  try {
    const alerts = root.querySelectorAll('.flash-messages .alert');
    alerts.forEach((el) => {
      // ensure we don't schedule twice
      if (el.dataset.autodismiss) return;
      el.dataset.autodismiss = '1';
      setTimeout(() => {
        try {
          const instance = bootstrap.Alert.getOrCreateInstance(el);
          instance.close();
        } catch (e) {
          // fallback: remove element
          el.remove();
        }
      }, 3000);
    });
  } catch (e) {}
}

// Observe new alerts added to the container
function observeAlerts() {
  const container = document.querySelector('.flash-messages');
  if (!container) return;
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        autoDismissAlerts(container);
      }
    }
  });
  mo.observe(container, { childList: true, subtree: true });
}

document.addEventListener('click', function (e) {
  if (!e.target) return;

  // Subject actions
  if (e.target.matches && e.target.matches('.icon-btn') && e.target.closest('.page-actions')) {
    // if button has n:onclick like attribute it's server-handled; otherwise handle by id
    const fn = e.target.getAttribute('data-action');
    if (fn === 'addSubject' || e.target.id === 'add-subject') {
      // placeholder: you can replace with modal open or navigation
      window.location.href = '/subject/new';
    }
  }

  if (e.target.matches && e.target.matches('#action-share')) {
    // placeholder: implement share dialog
    alert('Sdílet - implementujte podle potřeby');
  }
  if (e.target.matches && e.target.matches('#action-add-question')) {
    // placeholder
    window.location.href = window.location.pathname + '/questions/new';
  }
  if (e.target.matches && e.target.matches('#action-edit-subject')) {
    window.location.href = window.location.pathname + '/edit';
  }
});

function isEditableTarget(target) {
  try {
    if (!target) return false;
    // If a child element inside an input wrapper is focused, treat as editable
    if (target.closest && target.closest('input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"]')) {
      return true;
    }
    if (target.isContentEditable) return true;
    const tag = (target.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select';
  } catch (e) {
    return false;
  }
}

function isAnyModalOpen() {
  try {
    return !!document.querySelector('.modal.show');
  } catch (e) {
    return false;
  }
}

function pickTrigger(selectors) {
  try {
    for (const selector of selectors) {
      const candidates = Array.from(document.querySelectorAll(selector));
      if (!candidates.length) continue;

      // Prefer something visible/clickable (e.g. not inside a closed dropdown)
      const visible = candidates.find((el) => {
        try {
          return el && typeof el.getClientRects === 'function' && el.getClientRects().length > 0;
        } catch (e) {
          return false;
        }
      });
      return visible || candidates[0];
    }
    return null;
  } catch (e) {
    return null;
  }
}

function triggerModalByShortcut(kind) {
  const createSelectors = [
    '[data-bs-toggle="modal"][data-bs-target="#createQuestionModal"]',
    '[data-bs-toggle="modal"][data-bs-target="#createSubjectModal"]',
  ];
  const deleteSelectors = [
    '[data-bs-toggle="modal"][data-bs-target="#deleteQuestionModal"]',
    '[data-bs-toggle="modal"][data-bs-target="#deleteSubjectModal"]',
  ];

  const trigger = pickTrigger(kind === 'create' ? createSelectors : deleteSelectors);
  if (!trigger) return false;

  // Let existing template JS (capture handlers, relatedTarget logic, etc.) run.
  trigger.click();
  return true;
}

// init on load
document.addEventListener('DOMContentLoaded', function () {
  // Always apply dark theme
  document.documentElement.classList.add('theme-dark');
  
  autoDismissAlerts();
  observeAlerts();
  
  // init bootstrap tooltips
  try{
    var tooltipTriggerList = [].slice.call(document.querySelectorAll('[title]'));
    tooltipTriggerList.forEach(function (el) {
      bootstrap.Tooltip.getOrCreateInstance(el);
    });
  }catch(e){}

  // Initialize question search
  initQuestionSearch();

  // Initialize dropdown question search
  initDropdownQuestionSearch();

  // Funkce pro vypnutí obrazovky na Android zařízeních (dvojklik)
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Mozilla') && userAgent.includes('Android')) {
    let overlay = null;
    
    document.addEventListener('dblclick', function() {
      if (!overlay) {
        // Vytvoření černého překrytí
        overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.backgroundColor = 'black';
        overlay.style.zIndex = '999999';
        document.body.appendChild(overlay);
      } else {
        // Odstranění překrytí
        document.body.removeChild(overlay);
        overlay = null;
      }
    });
  }

  // Klávesové zkratky:
  // - Insert: otevře modal pro přidání (dle stránky)
  // - Delete: otevře modal pro smazání (dle stránky)
  document.addEventListener('keydown', function (e) {
    try {
      if (!e) return;
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isAnyModalOpen()) return;
      if (isEditableTarget(e.target)) return;

      const key = e.key;
      const code = e.code;

      const isInsert = key === 'Insert' || code === 'Insert';
      const isDelete = key === 'Delete' || key === 'Del' || code === 'Delete';

      if (!isInsert && !isDelete) return;

      const ok = triggerModalByShortcut(isInsert ? 'create' : 'delete');
      if (ok) e.preventDefault();
    } catch (err) {
      // no-op
    }
  });
});

// export example for bundlers
export function hello() {
  return 'Hello from main.js';
}

// Register service worker for PWA (if supported)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      // registration successful
      console.log('ServiceWorker registered: ', reg);
    }).catch(err => {
      console.log('ServiceWorker registration failed: ', err);
    });
  });
}

// PWA display detection and Install prompt handling
let __deferredPrompt = null;

function isStandalone() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch (e) {
    return false;
  }
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  __deferredPrompt = e;
  const banner = document.getElementById('pwa-install-banner');
  if (banner) banner.classList.remove('d-none');
});

window.addEventListener('appinstalled', () => {
  console.log('PWA installed');
  __deferredPrompt = null;
  const banner = document.getElementById('pwa-install-banner');
  if (banner) banner.classList.add('d-none');
});

document.addEventListener('DOMContentLoaded', function () {
  // mark standalone mode on root element
  if (isStandalone()) {
    document.documentElement.classList.add('is-standalone');
  }

  // install button handlers
  const installBtn = document.getElementById('pwa-install-btn');
  if (installBtn) {
    installBtn.addEventListener('click', async function () {
      if (!__deferredPrompt) return;
      __deferredPrompt.prompt();
      const choice = await __deferredPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') {
        console.log('User accepted the A2HS prompt');
      } else {
        console.log('User dismissed the A2HS prompt');
      }
      __deferredPrompt = null;
      const banner = document.getElementById('pwa-install-banner');
      if (banner) banner.classList.add('d-none');
    });
  }

  const installClose = document.getElementById('pwa-install-close');
  if (installClose) {
    installClose.addEventListener('click', function () {
      const banner = document.getElementById('pwa-install-banner');
      if (banner) banner.classList.add('d-none');
    });
  }

  // Learned questions toggle
  initLearnedQuestionsToggle();
  
  // Theme toggle
  initThemeToggle();

  // Share page charts (if present)
  initActivityCharts(document);
});

// Toggle learned status for questions
function initLearnedQuestionsToggle() {
  document.addEventListener('click', function(e) {
    const questionNumber = e.target.closest('.question-number');
    if (!questionNumber) return;

    e.preventDefault();
    e.stopPropagation();

    const questionItem = questionNumber.closest('.question-item');
    if (!questionItem) return;

    const questionId = questionItem.dataset.questionId;
    if (!questionId) return;

    // Optimistic UI update
    const isCurrentlyLearned = questionNumber.classList.contains('learned');
    
    // Toggle visual state immediately
    if (isCurrentlyLearned) {
      questionNumber.classList.remove('learned');
    } else {
      questionNumber.classList.add('learned');
    }

    // Send AJAX request
    fetch(`?do=toggleLearned&questionId=${questionId}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (!data.success) {
        // Revert on error
        if (isCurrentlyLearned) {
          questionNumber.classList.add('learned');
        } else {
          questionNumber.classList.remove('learned');
        }
        console.error('Toggle failed:', data.error);
      }
    })
    .catch(error => {
      // Revert on network error
      if (isCurrentlyLearned) {
        questionNumber.classList.add('learned');
      } else {
        questionNumber.classList.remove('learned');
      }
      console.error('Network error:', error);
    });
  });
}

// Theme toggle functionality
function initThemeToggle() {
  const themeToggle = document.getElementById('theme-toggle');
  const themeToggleText = document.getElementById('theme-toggle-text');
  
  if (!themeToggle) return;
  
  // Get current theme from cookie
  function getCurrentTheme() {
    const match = document.cookie.match(/theme=([^;]+)/);
    return match ? match[1] : 'dark';
  }
  
  // Set cookie with 1 year expiration
  function setThemeCookie(theme) {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    document.cookie = `theme=${theme}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
    console.log('Theme cookie set:', theme, document.cookie);
  }
  
  // Update UI text
  function updateThemeText(theme) {
    if (themeToggleText) {
      themeToggleText.textContent = theme === 'dark' ? 'Tmavý' : 'Světlý';
    }
  }
  
  // Initialize text based on current theme
  updateThemeText(getCurrentTheme());
  
  // Handle toggle click
  themeToggle.addEventListener('click', function(e) {
    e.preventDefault();
    
    const currentTheme = getCurrentTheme();
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    
    // Update body class
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add('theme-' + newTheme);
    
    // Update theme-color meta tag
    const themeColorMeta = document.getElementById('theme-color-meta');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', newTheme === 'light' ? '#ffffff' : '#0f1113');
    }
    
    // Update logos
    const logoSrc = newTheme === 'light' ? '/images/logo_light.svg' : '/images/logo_dark.svg';
    const logo = document.getElementById('logo');
    if (logo) logo.src = logoSrc;
    const logoHome = document.getElementById('logo-home');
    if (logoHome) logoHome.src = logoSrc;
    
    // Save to cookie
    setThemeCookie(newTheme);
    
    // Update text
    updateThemeText(newTheme);
  });
}

// Question search functionality
function initQuestionSearch() {
  const searchInput = document.getElementById('questionSearchInput');
  const clearBtn = document.getElementById('clearSearchBtn');
  const resultCount = document.getElementById('searchResultsCount');
  const questionItems = document.querySelectorAll('.question-item');

  if (!searchInput || questionItems.length === 0) return;

  function filterQuestions() {
    const searchTerm = searchInput.value.toLowerCase().trim();
    let visibleCount = 0;

    questionItems.forEach(item => {
      const titleAttr = item.getAttribute('data-question-title');
      if (!titleAttr) {
        console.warn('Missing data-question-title attribute on item:', item);
        return;
      }
      const title = titleAttr.toLowerCase();
      
      if (searchTerm === '' || title.includes(searchTerm)) {
        item.classList.remove('hidden-by-search');
        visibleCount++;
      } else {
        item.classList.add('hidden-by-search');
      }
    });

    // Show/hide clear button
    if (searchTerm !== '') {
      clearBtn.style.display = 'flex';
      resultCount.style.display = 'block';
      resultCount.textContent = `Zobrazeno ${visibleCount} z ${questionItems.length} otázek`;
    } else {
      clearBtn.style.display = 'none';
      resultCount.style.display = 'none';
    }
  }

  // Search on input
  searchInput.addEventListener('input', filterQuestions);

  // Clear button
  clearBtn.addEventListener('click', function() {
    searchInput.value = '';
    filterQuestions();
    searchInput.focus();
  });

  // Clear on Escape key
  searchInput.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      searchInput.value = '';
      filterQuestions();
    }
  });
}

// Dropdown question search functionality
function initDropdownQuestionSearch() {
  const searchInput = document.getElementById('dropdownSearchInput');
  const clearBtn = document.getElementById('clearDropdownSearchBtn');
  const questionItems = document.querySelectorAll('.question-dropdown-item');

  if (!searchInput || questionItems.length === 0) return;

  function filterQuestions() {
    const searchTerm = searchInput.value.toLowerCase().trim();

    questionItems.forEach(item => {
      const titleAttr = item.getAttribute('data-question-title');
      if (!titleAttr) return;
      
      const title = titleAttr.toLowerCase();
      
      if (searchTerm === '' || title.includes(searchTerm)) {
        item.classList.remove('hidden-by-search');
      } else {
        item.classList.add('hidden-by-search');
      }
    });

    // Show/hide clear button
    if (searchTerm !== '') {
      clearBtn.style.display = 'flex';
    } else {
      clearBtn.style.display = 'none';
    }
  }

  // Search on input
  searchInput.addEventListener('input', filterQuestions);

  // Clear button
  clearBtn.addEventListener('click', function() {
    searchInput.value = '';
    filterQuestions();
    searchInput.focus();
  });

  // Clear on Escape key
  searchInput.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      searchInput.value = '';
      filterQuestions();
    }
  });
}

